import { adminHomeDestination, isDealerShell } from '../pages/Admin/dealerAdminNav';
import { ROLES, ROUTES } from './constants';
import { canEditHomeButtons, isOperatorShellRole } from './sessionRole';
import { canManagePricing } from '../pages/Admin/pricingAccess';

const ADMIN_HOME_ROLES = new Set([ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]);

const HIDDEN_EXACT = new Set([
  ROUTES.LOGIN,
  ROUTES.BACKEND_LOGIN,
  ROUTES.BACKEND_DASHBOARD,
  ROUTES.AMBIENT,
]);

const HIDDEN_PREFIXES = [
  `${ROUTES.BACKEND_LOGIN}/`,
  `${ROUTES.BACKEND_DASHBOARD}/`,
  `${ROUTES.AMBIENT}/`,
];

export function isAdminRole(role) {
  return ADMIN_HOME_ROLES.has(String(role || ''));
}

export function isHomePath(pathname) {
  const path = String(pathname || '');
  return path === ROUTES.HOME || path === ROUTES.HOME_LAUNCHER || path === `${ROUTES.HOME_LAUNCHER}/`;
}

export function isHomeChromePath(pathname) {
  const path = String(pathname || '');
  if (isHomePath(path)) return true;
  return path === ROUTES.LAUNCHER_SETTINGS || path.startsWith(`${ROUTES.LAUNCHER_SETTINGS}/`);
}

export function isAdminPath(pathname) {
  const path = String(pathname || '');
  if (path === ROUTES.DASHBOARD || path.startsWith(`${ROUTES.DASHBOARD}/`)) return true;
  if (path === ROUTES.ADMIN || path.startsWith(`${ROUTES.ADMIN}/`)) return true;
  if (path === ROUTES.SETTINGS || path.startsWith(`${ROUTES.SETTINGS}/`)) return true;
  return false;
}

export function shouldShowAdminSidebar({ isAdmin = false, role, pathname, dealerActive = false, permissions = {}, supportAccess = false } = {}) {
  if (isOperatorShellRole(role)) return true;
  if (isHomeChromePath(pathname)) return false;
  if (isDealerShell({ pathname, dealerActive })) return true;
  if (canManagePricing({ role, permissions }) && String(pathname || '').startsWith('/admin/pricing')) return true;
  if (supportAccess) return true;
  return Boolean(isAdmin) || isAdminRole(role);
}

export function isTopChromeHiddenPath(pathname) {
  const path = String(pathname || '');
  if (!path || HIDDEN_EXACT.has(path)) return true;
  if (path === ROUTES.STUDIO || path.startsWith(`${ROUTES.STUDIO}/`)) return true;
  if (HIDDEN_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  return !isHomeChromePath(path);
}

export function getTopChromeActions({
  pathname,
  role,
  assistantEnabled = false,
  dealerActive = false,
  hasDealerAccess = false,
  dealerId = '',
  tenantId = '',
  permissions = {},
  algoDeviceSupport = false,
} = {}) {
  const home = isHomePath(pathname);
  const admin = isAdminRole(role);
  const dealerChrome = Boolean(dealerActive || hasDealerAccess);
  const canEditHome = canEditHomeButtons({ role, permissions, dealerActive: dealerChrome });

  if (isTopChromeHiddenPath(pathname)) {
    return {
      visible: false,
      showAgent: false,
      showAdminHome: false,
      showOperatorHome: false,
      showAddIcon: false,
      showPhone: false,
      showAssistant: false,
      showTraining: false,
      showRefresh: false,
      showSettings: false,
      settingsPath: ROUTES.SETTINGS,
      showSupport: false,
      showPower: false,
      adminHomePath: ROUTES.ADMIN_FLOWS,
    };
  }

  return {
    visible: true,
    showAgent: algoDeviceSupport === true,
    showAdminHome: (admin || dealerChrome) && home,
    showOperatorHome: !admin && !dealerChrome && !home,
    showAddIcon: home && canEditHome,
    showPhone: home,
    showAssistant: Boolean(assistantEnabled) && !home,
    showTraining: false,
    showRefresh: false,
    showSettings: home || admin,
    settingsPath: home ? ROUTES.LAUNCHER_SETTINGS : ROUTES.SETTINGS,
    showSupport: false,
    showPower: String(role || '') === ROLES.SUPERADMIN,
    adminHomePath: adminHomeDestination({
      role,
      dealerActive,
      dealerId,
      tenantId,
      hasDealerAccess,
    }),
  };
}
